/**
 * Environment Configuration
 *
 * This module manages environment-specific database configurations.
 *
 * STRATEGY:
 * 1. DEPLOYMENT (Vercel/Production):
 *    - Uses standard `NEXT_PUBLIC_SUPABASE_URL` and `SUPABASE_SERVICE_ROLE_KEY`.
 *    - Ignores `APP_ENV` to ensure immutable, predictable builds.
 *
 * 2. LOCAL DEVELOPMENT:
 *    - Controlled EXCLUSIVELY by `APP_ENV`.
 *    - Ignores `NEXT_PUBLIC_` variables to allow authenticating against Prod/Staging from local.
 *    - IF `APP_ENV=production`, uses `PROD_SUPABASE_*` variables.
 */

type Environment = 'development' | 'staging' | 'production'

interface SupabaseConfig {
  url: string
  anonKey: string
  serviceRoleKey: string
}

/**
 * Get the current application environment
 */
export function getAppEnvironment(): Environment {
  const env = process.env.APP_ENV?.toLowerCase() as Environment

  if (env === 'production' || env === 'staging' || env === 'development') {
    return env
  }

  // Default to development for safety
  if (process.env.NODE_ENV !== 'production' && process.env.APP_ENV) {
    console.warn(`Invalid APP_ENV value: ${process.env.APP_ENV}. Defaulting to 'development'`)
  }
  return 'development'
}

/**
 * Get Supabase configuration based on context
 */
export function getSupabaseConfig(): SupabaseConfig {
  // ---------------------------------------------------------------------------
  // 1. DEPLOYMENT CONTEXT (Vercel / Production Entry)
  // ---------------------------------------------------------------------------
  // We determine "Deployment" if NODE_ENV is production AND standard vars are present.
  // This ensures Vercel builds use the environment variables configured in the dashboard.
  const isProductionBuild = process.env.NODE_ENV === 'production'
  const deploymentUrl = process.env.NEXT_PUBLIC_SUPABASE_URL?.trim()
  const deploymentAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim()
  const deploymentServiceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY?.trim()

  if (isProductionBuild && deploymentUrl && deploymentAnonKey && deploymentServiceRoleKey) {
    return {
      url: deploymentUrl,
      anonKey: deploymentAnonKey,
      serviceRoleKey: deploymentServiceRoleKey,
    }
  }

  // ---------------------------------------------------------------------------
  // 1b. PRODUCTION BUILD WITHOUT COMPLETE DEPLOYMENT VARIABLES
  // ---------------------------------------------------------------------------
  // If NODE_ENV is 'production' but deployment variables are incomplete:
  //  - If ANY standard deployment var is present, this is a real deployment
  //    with partial misconfiguration — fail fast with a clear error.
  //  - If NONE are present AND APP_ENV is not set, also fail fast.
  //  - If NONE are present AND APP_ENV IS set, allow fallthrough to
  //    local dev config (supports `next start` locally with APP_ENV).
  if (isProductionBuild) {
    const hasAnyDeploymentVar = !!(deploymentUrl || deploymentAnonKey || deploymentServiceRoleKey)

    if (hasAnyDeploymentVar || !process.env.APP_ENV) {
      const missing: string[] = []
      if (!deploymentUrl) missing.push('NEXT_PUBLIC_SUPABASE_URL')
      if (!deploymentAnonKey) missing.push('NEXT_PUBLIC_SUPABASE_ANON_KEY')
      if (!deploymentServiceRoleKey) missing.push('SUPABASE_SERVICE_ROLE_KEY')

      throw new Error(
        `Production deployment is missing required Supabase environment variables:\n` +
          missing.map((v) => ` - ${v}`).join('\n') +
          `\nEnsure these are configured in your deployment platform (e.g., Vercel dashboard).`
      )
    }
  }

  // ---------------------------------------------------------------------------
  // 2. LOCAL DEVELOPMENT CONTEXT
  // ---------------------------------------------------------------------------
  // In local dev, we strictly obey APP_ENV. We do NOT fall back to NEXT_PUBLIC_*
  // variables mixed in with local config to avoid confusion.

  const env = getAppEnvironment()
  let url: string | undefined
  let anonKey: string | undefined
  let serviceRoleKey: string | undefined

  switch (env) {
    case 'production':
      url = process.env.PROD_SUPABASE_URL
      anonKey = process.env.PROD_SUPABASE_ANON_KEY
      serviceRoleKey = process.env.PROD_SUPABASE_SERVICE_ROLE_KEY
      break

    case 'staging':
      url = process.env.STAGING_SUPABASE_URL
      anonKey = process.env.STAGING_SUPABASE_ANON_KEY
      serviceRoleKey = process.env.STAGING_SUPABASE_SERVICE_ROLE_KEY
      break

    case 'development':
    default:
      url = process.env.DEV_SUPABASE_URL
      anonKey = process.env.DEV_SUPABASE_ANON_KEY
      serviceRoleKey = process.env.DEV_SUPABASE_SERVICE_ROLE_KEY
      break
  }

  // Validate that required values are present for the requested local environment
  if (!url || !anonKey || !serviceRoleKey) {
    let prefix = 'DEV'
    if (env === 'production') prefix = 'PROD'
    if (env === 'staging') prefix = 'STAGING'

    throw new Error(
      `Missing configuration for APP_ENV=${env}.\n` +
        `Please ensure your .env file has:\n` +
        ` - ${prefix}_SUPABASE_URL\n` +
        ` - ${prefix}_SUPABASE_ANON_KEY\n` +
        ` - ${prefix}_SUPABASE_SERVICE_ROLE_KEY`
    )
  }

  return { url, anonKey, serviceRoleKey }
}

/**
 * Get public Supabase configuration for client-side code
 */
export function getPublicSupabaseConfig(): { url: string; anonKey: string } {
  const deploymentUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const deploymentAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

  // ---------------------------------------------------------------------------
  // 1. CLIENT-SIDE (Browser): ALWAYS use NEXT_PUBLIC_* variables
  // ---------------------------------------------------------------------------
  // In the browser, we can ONLY read NEXT_PUBLIC_* variables.
  // These are injected by next.config.ts based on APP_ENV during local dev,
  // or set directly in Vercel for production.
  if (typeof window !== 'undefined') {
    if (!deploymentUrl || !deploymentAnonKey) {
      throw new Error(
        'Missing NEXT_PUBLIC_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_ANON_KEY.\n' +
          'These should be injected automatically by next.config.ts based on APP_ENV.\n' +
          'Ensure your .env file has the correct *_SUPABASE_URL and *_SUPABASE_ANON_KEY for your APP_ENV setting.'
      )
    }
    return { url: deploymentUrl, anonKey: deploymentAnonKey }
  }

  // ---------------------------------------------------------------------------
  // 2. SERVER-SIDE: Use full config logic (can read all env vars)
  // ---------------------------------------------------------------------------
  // On the server, we can access all environment variables, so we use the
  // main getSupabaseConfig() function for consistency.
  const config = getSupabaseConfig()
  return { url: config.url, anonKey: config.anonKey }
}

/**
 * Redis is required only on Vercel's multi-instance runtime. A local
 * `next build && next start` sets NODE_ENV=production and has no Redis by
 * design; using NODE_ENV alone locks login on the Mac mini.
 */
export function isHostedMultiInstance(): boolean {
  const vercelEnv = process.env.VERCEL_ENV
  return (
    process.env.NODE_ENV === 'production' && (vercelEnv === 'production' || vercelEnv === 'preview')
  )
}

/**
 * Fail the deploy instead of the login. Since 2026-09-02 login, signup and
 * password reset fail closed without Redis (lib/security/rate-limit.ts), so a
 * deployment missing these two variables looks healthy and locks every user out
 * one request at a time. Only asserted on Vercel (VERCEL_ENV production /
 * preview): a local `next build && next start` has no Redis by design.
 */
export function assertRuntimeSecrets(): void {
  if (!isHostedMultiInstance()) return
  const vercelEnv = process.env.VERCEL_ENV

  const missing: string[] = []
  if (!process.env.UPSTASH_REDIS_REST_URL?.trim()) missing.push('UPSTASH_REDIS_REST_URL')
  if (!process.env.UPSTASH_REDIS_REST_TOKEN?.trim()) missing.push('UPSTASH_REDIS_REST_TOKEN')

  if (missing.length > 0) {
    throw new Error(
      `Deployment (VERCEL_ENV=${vercelEnv}) is missing required rate-limit environment variables:\n` +
        missing.map((v) => ` - ${v}`).join('\n') +
        `\nWithout Redis, login / signup / password reset fail closed for every user.`
    )
  }
}

/**
 * Log current environment on startup
 */
if (typeof window === 'undefined') {
  assertRuntimeSecrets()
  // Only log on server-side
  const isProductionBuild = process.env.NODE_ENV === 'production'

  if (isProductionBuild) {
    // Quiet in true production to keep logs clean, or log standard msg
    // console.log(`🚀 Running in DEPLOYMENT mode`)
  } else {
    const env = getAppEnvironment()
    console.log(`\n-------------------------------------------------------------`)
    console.log(`🔧 Local Mode Active: ${env.toUpperCase()}`)
    console.log(`   Connecting to: ${env.toUpperCase()} database`)
    console.log(`   (Ignoring NEXT_PUBLIC_* variables in .env if present)`)
    console.log(`-------------------------------------------------------------\n`)
  }
}
